import React, { useRef } from 'react';
import styled from 'styled-components';
import { useReactToPrint } from 'react-to-print';

const toDollarVal = (val) => {
  if (val === undefined || val === null || val === '') return 0;
  if (typeof val === 'string' && val.startsWith('$')) {
    const p = parseFloat(val.replace('$', ''));
    return isNaN(p) ? 0 : p;
  }
  const n = Number(val);
  if (isNaN(n)) return 0;
  if (n === 0) return 0;

  // Raw amounts in this application (> 100 or integers) are in cents.
  // E.g. tax in cents can be floating like 410.97 cents (which is $4.11).
  if (n > 100 || Number.isInteger(n)) {
    return n / 100;
  }
  return n;
};

const TaxInvoiceModal = ({ order, onClose }) => {
  const invoiceRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: `Tax_Invoice_${order.id ? order.id.slice(0, 8) : 'order'}`
  });

  if (!order) return null;

  const items = order.items || (order.products && order.products.items) || [];
  const billing = order.billingDetails || {};
  const shipping = order.shippingAddress || (billing.address ? billing : {});

  // Calculate items subtotal as fallback if subtotal is missing
  const calculatedItemsSubtotal = items.reduce((sum, item) => {
    const unitPrice = toDollarVal(item.price);
    const qty = item.quantity || 1;
    return sum + (unitPrice * qty);
  }, 0);

  const rawSubtotal = order.subtotal !== undefined
    ? order.subtotal
    : (order.init_price !== undefined ? order.init_price : calculatedItemsSubtotal);

  const subtotal = toDollarVal(rawSubtotal);
  const tax = toDollarVal(order.tax !== undefined ? order.tax : order.tax_price);
  const total = toDollarVal(order.price !== undefined ? order.price : order.totalAmount) || (subtotal + tax);

  const invoiceNum = `INV-${(order.id || 'ORDER').slice(-8).toUpperCase()}`;
  const invoiceDate = order.orderDate || (order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderTitle>
            <span role="img" aria-label="invoice">📄</span> Tax Invoice Ready
          </HeaderTitle>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </ModalHeader>

        <AlertBanner>
          <span role="img" aria-label="email">✉️</span> A copy of this tax invoice has been automatically generated and sent to your email!
        </AlertBanner>

        <InvoiceScrollArea>
          {/* Printable Invoice Container */}
          <PrintArea ref={invoiceRef}>
            <InvoiceHeader>
              <CompanyInfo>
                <BrandName>Bombay Trading Co.</BrandName>
                <CompanySub>Premium Indian Sweets & Groceries</CompanySub>
                <CompanyMeta>GST / Tax Reg No: TAX-US-8894210</CompanyMeta>
                <CompanyMeta>Email: support@bombaytrading.com | Web: bombaytrading.com</CompanyMeta>
              </CompanyInfo>
              <InvoiceMeta>
                <InvoiceTitle>TAX INVOICE</InvoiceTitle>
                <MetaBadge>PAID</MetaBadge>
                <InvoiceNumber>{invoiceNum}</InvoiceNumber>
                <InvoiceDate>Date: {invoiceDate}</InvoiceDate>
              </InvoiceMeta>
            </InvoiceHeader>

            <Divider />

            <AddressGrid>
              <AddressBlock>
                <BlockTitle>Billed To</BlockTitle>
                <PersonName>{billing.name || (order.user && order.user.username) || 'Valued Customer'}</PersonName>
                <DetailText>{billing.email || (order.user && order.user.email) || ''}</DetailText>
              </AddressBlock>

              <AddressBlock>
                <BlockTitle>Shipped To</BlockTitle>
                <PersonName>{shipping.fullName || billing.name || 'Valued Customer'}</PersonName>
                <DetailText>{shipping.address_line1 || shipping.addressLine1 || (billing.address && billing.address.line1) || ''}</DetailText>
                <DetailText>
                  {shipping.city || (billing.address && billing.address.city) || ''}, {shipping.state || shipping.address_state || (billing.address && billing.address.state) || ''} {shipping.zip || shipping.address_zip || (billing.address && billing.address.postal_code) || ''}
                </DetailText>
                {shipping.phone_number && <DetailText>Phone: {shipping.phone_number}</DetailText>}
              </AddressBlock>
            </AddressGrid>

            <Table>
              <thead>
                <tr>
                  <Th style={{ width: '5%' }}>#</Th>
                  <Th style={{ width: '55%' }}>Item Description</Th>
                  <Th style={{ width: '12%', textAlign: 'center' }}>Qty</Th>
                  <Th style={{ width: '14%', textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ width: '14%', textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const unitPrice = toDollarVal(item.price);
                  const qty = item.quantity || 1;
                  const itemTotal = (unitPrice * qty).toFixed(2);

                  return (
                    <tr key={item.id || idx}>
                      <Td style={{ color: '#8a99a8' }}>{idx + 1}</Td>
                      <Td style={{ fontWeight: '600', color: '#1f2d3d' }}>
                        {item.description || item.name || 'Product Item'}
                      </Td>
                      <Td style={{ textAlign: 'center' }}>{qty}</Td>
                      <Td style={{ textAlign: 'right' }}>${unitPrice.toFixed(2)}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: '700' }}>${itemTotal}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <SummaryGrid>
              <NotesBox>
                <NotesTitle>Payment Details</NotesTitle>
                <NotesText>Payment Status: <strong>Completed (PAID)</strong></NotesText>
                <NotesText>Payment Intent Ref: {order.paymentIntent || 'PI_ONLINE'}</NotesText>
                <NotesText>Thank you for shopping with Bombay Trading Co.!</NotesText>
              </NotesBox>

              <TotalsTable>
                <TotalRow>
                  <TotalLabel>Subtotal:</TotalLabel>
                  <TotalVal>${subtotal.toFixed(2)}</TotalVal>
                </TotalRow>
                <TotalRow>
                  <TotalLabel>Estimated Tax:</TotalLabel>
                  <TotalVal>${tax.toFixed(2)}</TotalVal>
                </TotalRow>
                <TotalRow>
                  <TotalLabel>Shipping:</TotalLabel>
                  <TotalVal>$0.00 (FREE)</TotalVal>
                </TotalRow>
                <Divider style={{ margin: '8px 0' }} />
                <GrandTotalRow>
                  <GrandTotalLabel>Grand Total:</GrandTotalLabel>
                  <GrandTotalVal>${total.toFixed(2)}</GrandTotalVal>
                </GrandTotalRow>
              </TotalsTable>
            </SummaryGrid>

            <FooterText>
              This is a computer-generated tax invoice. No signature is required.
            </FooterText>
          </PrintArea>
        </InvoiceScrollArea>

        <ActionBar>
          <PrintBtn onClick={handlePrint}>
            <span role="img" aria-label="print">📄</span> Download / Print PDF Invoice
          </PrintBtn>
          <DoneBtn onClick={onClose}>Close</DoneBtn>
        </ActionBar>
      </ModalCard>
    </ModalBackdrop>
  );
};

export default TaxInvoiceModal;

/* ---------- Styles ---------- */
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 20px;
`;

const ModalCard = styled.div`
  background: #fff;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafbfc;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1f2d3d;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  font-size: 1.6rem;
  color: #8a99a8;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #1f2d3d;
  }
`;

const AlertBanner = styled.div`
  background: #e6fffa;
  border-bottom: 1px solid #b2f5ea;
  color: #234e52;
  padding: 10px 24px;
  font-size: 0.88rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InvoiceScrollArea = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const PrintArea = styled.div`
  background: #fff;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: #01bf71;
  margin: 0 0 4px;
`;

const CompanySub = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: #4a5568;
`;

const CompanyMeta = styled.span`
  font-size: 0.78rem;
  color: #718096;
  margin-top: 2px;
`;

const InvoiceMeta = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InvoiceTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 900;
  color: #1a202c;
  margin: 0 0 4px;
  letter-spacing: 0.05em;
`;

const MetaBadge = styled.span`
  background: #c6f6d5;
  color: #22543d;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  display: inline-block;
  margin-bottom: 6px;
`;

const InvoiceNumber = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d3748;
`;

const InvoiceDate = styled.span`
  font-size: 0.82rem;
  color: #718096;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 20px 0;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
`;

const AddressBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 800;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const PersonName = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a202c;
`;

const DetailText = styled.span`
  font-size: 0.85rem;
  color: #4a5568;
  margin-top: 2px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
`;

const Th = styled.th`
  background: #f7fafc;
  border-bottom: 2px solid #e2e8f0;
  padding: 10px 12px;
  font-size: 0.78rem;
  font-weight: 800;
  color: #4a5568;
  text-transform: uppercase;
  text-align: left;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #edf2f7;
  font-size: 0.88rem;
  color: #2d3748;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 20px;
  align-items: flex-start;
`;

const NotesBox = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  padding: 14px;
  border: 1px solid #edf2f7;
`;

const NotesTitle = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: #2d3748;
  display: block;
  margin-bottom: 4px;
`;

const NotesText = styled.p`
  font-size: 0.78rem;
  color: #718096;
  margin: 2px 0;
`;

const TotalsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
`;

const TotalLabel = styled.span`
  color: #718096;
`;

const TotalVal = styled.span`
  color: #2d3748;
  font-weight: 600;
`;

const GrandTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GrandTotalLabel = styled.span`
  font-size: 1.05rem;
  font-weight: 800;
  color: #1a202c;
`;

const GrandTotalVal = styled.span`
  font-size: 1.25rem;
  font-weight: 900;
  color: #01bf71;
`;

const FooterText = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 30px;
`;

const ActionBar = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(31, 45, 61, 0.1);
  background: #fafbfc;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const PrintBtn = styled.button`
  background: #01bf71;
  color: #fff;
  border: none;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 10px 22px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(1, 191, 113, 0.35);
  transition: all 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(1, 191, 113, 0.42);
  }
`;

const DoneBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(31, 45, 61, 0.2);
  color: #24313f;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(31, 45, 61, 0.05);
  }
`;
